/**
 * `/sites/$siteId/pages/$pageId` — PageVersion lifecycle editor (issue #172).
 *
 * Closes the demo-blocking gap left open by #11/#12/#13: the API exposes
 * publish / unpublish / rollback / draft-from endpoints, but until this
 * screen there was no in-browser way to drive them. The operator can now
 * land here from `SiteDetailPage`'s Pages tab, edit the draft's
 * `blocksOrderJson` via a structured block-list editor, save it,
 * publish it, unpublish it, or roll back to an older version.
 *
 * Layout — three lifecycle slots, top-to-bottom in lifecycle order:
 *
 *   1. **Current draft**       — may be null (after publish the draft row
 *                                transitions to PUBLISHED, so no draft
 *                                exists until a new one is minted). When
 *                                present: structured block list + Save /
 *                                Publish, with an Advanced toggle that
 *                                exposes the raw JSON + schema reference
 *                                for power users. When absent: a recovery
 *                                card surfaces the most-recent archived
 *                                row as a one-click "Restore as draft"
 *                                CTA so the operator is never stranded
 *                                (issue #186).
 *   2. **Currently published** — may be null. Shows version metadata, an
 *                                Unpublish action, and a "Create new
 *                                draft from this" amendment action.
 *   3. **Archived versions**   — read-only summary with a Rollback button
 *                                per row that seeds a new draft from the
 *                                archived payload. Auto-expands when the
 *                                page has neither a draft nor a published
 *                                version (the unpublish-recovery state) so
 *                                the operator can see their options without
 *                                hunting for the toggle.
 *
 * Issue #256 — the Draft slot now leads with a structured `<BlocksListEditor>`
 * (per-row drag handle, Up/Down, Edit link, Remove, plus an Add Autocomplete).
 * The block list is the in-memory source of truth; the raw JSON textarea is
 * preserved behind an Advanced toggle for power users and two-way binds to
 * the block list. The previous standalone `ContentItemsBrowser` table is
 * gone — its "Insert" action becomes the Add Autocomplete inside the editor.
 *
 * 409 `VERSION_RACE` handling: the publish mutation surfaces the typed
 * error code; we render a `warning`-severity toast ("Another publish
 * raced you — refreshing.") and explicitly refetch. Per the issue we do
 * NOT show the generic error UI on this code.
 *
 * MUI only — no Tailwind, CSS modules, or raw CSS. The Advanced JSON
 * textarea uses `TextField` with `multiline` and an explicit monospace
 * `sx` because the issue grants a styled `<textarea>` for v1 (Monaco /
 * CodeMirror are out-of-scope).
 *
 * `data-testid` follows the SiteDetailPage convention (`page-detail-*`)
 * so the QA agent can attach Playwright specs in a follow-up.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link, useParams } from "@tanstack/react-router";
import {
  blocksOrderV1,
  type PageVersionResponse,
  type PageWithVersionsResponse,
} from "@ottopilot/shared";
import { useSite } from "../hooks/useSites";
import { useDomains } from "../hooks/useDomains";
import { buildPublicUrl, findPrimaryDomain } from "../lib/publicUrl";
import {
  useArchiveExperimentPageVersion,
  useDraftFromVersion,
  usePage,
  usePromotePageVersion,
  usePublishPageVersion,
  useUnpublishPageVersion,
  useUpdatePageVersion,
} from "../hooks/usePageVersions";
import { useContentItemsForSite } from "../hooks/useContentItems";
import { useCreatePreviewToken } from "../hooks/usePreviewToken";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import type { ApiClientError } from "../lib/api";
import { PageLayoutGeneratorDialog } from "../features/ai/PageLayoutGeneratorDialog";
import { ExperimentBacklinks } from "../features/experiments/ExperimentBacklinks";
import { BlocksListEditor, type BlockRef } from "../features/pages/BlocksListEditor";

/**
 * Page detail response. `archived` is now part of the shared contract
 * (issue #186) and always present — the empty array signals "no archived
 * versions yet", not "the API forgot to populate this".
 */
type PageDetail = PageWithVersionsResponse;

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * Pretty-print arbitrary JSON for the textarea seed. Falls back to the
 * raw `String(value)` when serialisation throws (which it shouldn't for
 * the JSON-typed Prisma field, but we don't want a render-crash for a
 * malformed legacy row). Two-space indent is the canonical project style.
 */
function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Example payload for the schema-reference card (rendered inside the
 * Advanced toggle). Built by feeding a plausible block reference through
 * the same shared Zod schema, so the example is guaranteed to be a
 * *valid* `blocksOrderV1` payload. The `contentItemId` placeholder is
 * rendered as a ULID-shaped string — same shape `ContentItem.id` takes
 * — so the operator can visually pattern-match against ids in the
 * structured editor above.
 */
const SCHEMA_REFERENCE_EXAMPLE_TEXT = JSON.stringify(
  blocksOrderV1.parse({
    schemaVersion: 1,
    blocks: [{ contentItemId: "ckxxxxxxxxxxxxxxxxxxxxxxxx" }],
  }),
  null,
  2,
);

/**
 * Serialize an in-memory block list to the canonical `blocksOrderV1`
 * envelope and return its pretty-printed JSON form. Used by `DraftSlot`
 * to keep the Advanced JSON textarea in sync with the structured editor
 * above it — every edit applied to `blocks` rebuilds this string. Two-
 * space indent matches `prettyJson` everywhere else on the page.
 */
function serializeBlocks(blocks: BlockRef[]): string {
  return JSON.stringify({ schemaVersion: 1, blocks }, null, 2);
}

/**
 * Try to extract a `BlockRef[]` from arbitrary JSON the operator typed
 * into the Advanced textarea. Returns the parsed blocks when the input
 * is a valid `blocksOrderV1` envelope, or `null` when it isn't — the
 * caller surfaces an Alert and leaves the structured editor in its
 * last-valid state.
 */
function tryParseBlocks(text: string): BlockRef[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return null;
  }
  const check = blocksOrderV1.safeParse(parsed);
  if (!check.success) return null;
  return check.data.blocks.map((b) => ({ contentItemId: b.contentItemId }));
}

/**
 * Extract the initial `BlockRef[]` from a draft's persisted
 * `blocksOrderJson`. Returns an empty array when the field is missing,
 * malformed, or absent — operators see the empty state and can rebuild
 * the page rather than being stranded on a parse error.
 */
function blocksFromDraftJson(blocksOrderJson: unknown): BlockRef[] {
  const check = blocksOrderV1.safeParse(blocksOrderJson);
  if (!check.success) return [];
  return check.data.blocks.map((b) => ({ contentItemId: b.contentItemId }));
}

function statusChipColor(
  status: PageVersionResponse["status"],
): "default" | "primary" | "success" | "warning" {
  switch (status) {
    case "DRAFT":
      return "primary";
    case "PUBLISHED":
      return "success";
    case "EXPERIMENT":
      // Issue #243: EXPERIMENT is approved for experiment traffic but
      // not the default render. `warning` (yellow) distinguishes it
      // from the green PUBLISHED chip at a glance.
      return "warning";
    case "ARCHIVED":
      return "default";
    default:
      return "default";
  }
}

/**
 * Draft slot — structured block-list editor + Save / Publish, with an
 * Advanced toggle that exposes the raw `blocksOrderJson` textarea and
 * the schema-reference panel for power users (issue #256).
 *
 * The block list is the canonical in-memory state; the Advanced textarea
 * is a derived view that two-way-binds back to it. When the operator
 * edits the textarea we attempt to parse it: on success the block list
 * above syncs to the parsed blocks; on failure we surface an Alert and
 * leave the block list in its last-valid state.
 *
 * Save / Publish semantics are unchanged: Save is disabled when the
 * block list matches the persisted seed (no diff) or when the Advanced
 * textarea is in a parse-error state. Publish is enabled only when the
 * block list matches the persisted seed — an unsaved edit forces a Save
 * first so the operator never publishes something different from what
 * they see.
 */
function DraftSlot({
  pageId,
  siteId,
  draft,
  blocks,
  setBlocks,
  onAfterDraftFromPublished,
  previewHostname = null,
}: {
  pageId: string;
  siteId: string;
  draft: PageVersionResponse;
  blocks: BlockRef[];
  setBlocks: (next: BlockRef[]) => void;
  onAfterDraftFromPublished?: () => void;
  /**
   * Primary public hostname for this site, when present. Enables a draft
   * Preview affordance pointing at `/preview/page/:draftVersionId?t=<jwt>`
   * — same token contract as the archived-row Preview, so the route's
   * cross-site leak guard + token-or-cookie auth still apply. Hidden when
   * the site has no primary domain to anchor the preview URL to.
   */
  previewHostname?: string | null;
}): ReactElement {
  const toast = useToast();
  const update = useUpdatePageVersion(pageId);
  const publish = usePublishPageVersion(pageId);
  const createPreviewToken = useCreatePreviewToken();

  // ContentItems list query. Reused for (a) the BlocksListEditor's row
  // labels and Add Autocomplete and (b) the Advanced JSON view's lookup
  // hints. We load page 1, pageSize 100 — the same per-site cap that
  // `blocksOrderV1` enforces on the block list itself, so any
  // legitimately-available item shows up in the Autocomplete without a
  // second page request. If the site exceeds 100 ContentItems we'll
  // need server-side search; that's a deferred follow-up.
  const contentItems = useContentItemsForSite(siteId, { page: 1, pageSize: 100 });

  // #208 token mint → window.open with `?t=<jwt>`. Identical contract to
  // ArchivedSlot's preview path so the public route doesn't need a draft-
  // specific code path.
  const handlePreviewDraft = (hostname: string) => {
    createPreviewToken.mutate(
      { resourceType: "page-version", resourceId: draft.id },
      {
        onSuccess: ({ token }) => {
          const url = buildPublicUrl(hostname, `/preview/page/${draft.id}?t=${token}`);
          window.open(url, "_blank", "noopener,noreferrer");
        },
        onError: (err) => toast.showError(err.message || "Could not open preview."),
      },
    );
  };
  // Issue #181: dialog state for the "Generate with AI" affordance. The
  // dialog handles the prompt + mutation lifecycle itself; we only own
  // the open/closed flag here. After a successful generation, the
  // `useGenerateLayout` mutation invalidates `pages.detail(pageId)`,
  // which refetches and re-seeds the block list via the sync effect
  // below.
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  // Advanced toggle (collapsed by default). Hides the JSON textarea +
  // schema reference panel so the structured editor dominates the
  // screen for the common case. Persisted only in component state —
  // open/closed across refreshes is intentionally not sticky; the
  // common operator never needs to see this.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Canonical pretty-printed seed from the server. Recomputed whenever
  // the draft's `blocksOrderJson` reference changes (TanStack Query
  // hands us a fresh object after every refetch that produced new
  // bytes), so an AI-accept PATCH or a foreign update from another tab
  // both re-key the seed even though `draft.id` stays the same.
  const seedJson = useMemo(() => prettyJson(draft.blocksOrderJson), [draft.blocksOrderJson]);
  // Track the last server-side seed we synced from. We resync the
  // block list whenever that string changes — covering (a) initial
  // mount, (b) a new draft row via draft-from / rollback, AND (c) an
  // in-place server payload change (AI accept #181, foreign update from
  // another tab). The invariant: server-driven seed changes win, but
  // local edits never re-trigger this effect because `seedJson` is
  // derived from the server payload, not from `blocks`.
  const lastSyncedSeed = useRef<string | null>(null);

  // Advanced JSON textarea state. Mirrors `blocks` whenever the
  // structured editor changes; the operator can also hand-edit it,
  // which we parse back into `blocks` on every change.
  const [advancedText, setAdvancedText] = useState<string>(() => serializeBlocks(blocks));
  // Whether the textarea is currently in a parse-error state. When
  // true, we hold off on updating `blocks` from textarea edits and
  // surface an Alert so the operator can fix the JSON.
  const [advancedError, setAdvancedError] = useState<string | null>(null);

  useEffect(() => {
    if (lastSyncedSeed.current !== seedJson) {
      lastSyncedSeed.current = seedJson;
      const seededBlocks = blocksFromDraftJson(draft.blocksOrderJson);
      setBlocks(seededBlocks);
      setAdvancedText(seedJson);
      setAdvancedError(null);
    }
  }, [seedJson, draft.blocksOrderJson, setBlocks]);

  // Whenever the block list changes (operator edit OR server-driven
  // seed flip), regenerate the Advanced textarea text to match. We
  // only do this when the Advanced textarea is in a valid state — if
  // the operator is mid-typing-invalid-JSON, overwriting their work
  // would be hostile. The error state is cleared by their next valid
  // edit or by the next seed sync.
  const serializedBlocks = useMemo(() => serializeBlocks(blocks), [blocks]);
  useEffect(() => {
    if (advancedError === null) {
      setAdvancedText(serializedBlocks);
    }
  }, [serializedBlocks, advancedError]);

  const isUnchanged = serializedBlocks === seedJson;
  const hasParseError = advancedError !== null;
  const canSave = !isUnchanged && !hasParseError && !update.isPending;
  const canPublish = isUnchanged && !hasParseError && !publish.isPending;

  const handleBlocksChange = (next: BlockRef[]) => {
    setBlocks(next);
    // Editing the structured list always clears any prior Advanced
    // parse error — the textarea will be overwritten by the
    // `serializedBlocks` effect above.
    setAdvancedError(null);
  };

  const handleAdvancedTextChange = (next: string) => {
    setAdvancedText(next);
    const parsedBlocks = tryParseBlocks(next);
    if (parsedBlocks === null) {
      setAdvancedError(
        "Invalid JSON or schema. The block list above is preserved — fix the JSON to sync.",
      );
      return;
    }
    setAdvancedError(null);
    setBlocks(parsedBlocks);
  };

  const handleSave = () => {
    if (hasParseError) return;
    update.mutate(
      {
        versionId: draft.id,
        // The shared `updatePageVersionSchema` accepts `blocksOrderJson`
        // as the structured payload. We serialize the in-memory block
        // list (not the Advanced text) — the block list is the source
        // of truth.
        input: {
          blocksOrderJson: { schemaVersion: 1, blocks } as never,
        },
      },
      {
        onSuccess: () => {
          toast.showSuccess("Draft saved.");
          // After save the persisted JSON is the new seed. We let the
          // `seedJson` effect above resync once the query invalidation
          // lands; in the meantime, force-resync the Advanced textarea
          // to the canonical serialized form so the dirty indicator
          // clears immediately even before the refetch resolves.
          setAdvancedText(serializeBlocks(blocks));
          setAdvancedError(null);
        },
        onError: (err) => {
          if (err.code === "INVALID_BLOCKS_ORDER") {
            toast.showError(
              "Some referenced content items are invalid. Check the block references.",
            );
            return;
          }
          toast.showError(err.message || "Could not save draft.");
        },
      },
    );
  };

  // Issue #243: publish dialog. The operator chooses between "Make
  // this the primary" (DRAFT → PUBLISHED, demotes prior PUBLISHED) and
  // "Publish for experiment use" (DRAFT → EXPERIMENT, no demotion).
  // The dialog defaults to "primary" so the historical one-click
  // workflow still works — the operator just has to confirm.
  const [publishDialogMode, setPublishDialogMode] = useState<"primary" | "experiment">("primary");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const handleOpenPublishDialog = () => {
    setPublishDialogMode("primary");
    setPublishDialogOpen(true);
  };

  const handleConfirmPublish = () => {
    const mode = publishDialogMode;
    setPublishDialogOpen(false);
    publish.mutate(
      { versionId: draft.id, mode },
      {
        onSuccess: () => {
          toast.showSuccess(
            mode === "experiment" ? "Draft published for experiment use." : "Draft published.",
          );
        },
        onError: (err: ApiClientError) => {
          if (err.code === "VERSION_RACE") {
            // Yellow toast (warning) per the issue brief — the refetch
            // is triggered by the mutation's onSuccess-side invalidation
            // already, but we explicitly fire it again here so even a
            // hard-failure path lands the operator on fresh data.
            toast.show({
              message: "Another publish raced you — refreshing.",
              severity: "warning",
            });
            onAfterDraftFromPublished?.();
            return;
          }
          toast.showError(err.message || "Could not publish draft.");
        },
      },
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} data-testid="page-detail-draft-slot">
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3">Current draft</Typography>
            <Chip
              size="small"
              color={statusChipColor(draft.status)}
              label={draft.status}
              variant="outlined"
              data-testid="page-detail-draft-status-chip"
            />
            <Typography variant="body2" color="text.secondary">
              v{draft.versionNumber}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Created {formatDateTime(draft.createdAt)} · Updated {formatDateTime(draft.updatedAt)}
          </Typography>
        </Stack>

        <BlocksListEditor
          blocks={blocks}
          onChange={handleBlocksChange}
          siteId={siteId}
          contentItems={contentItems.data?.data}
          contentItemsLoading={contentItems.isPending}
          contentItemsError={contentItems.isError ? contentItems.error.message : null}
        />

        {/* Dirty state indicator — mirrors the legacy textarea helperText
            so operators get the same "Unsaved changes" cue they relied on
            before. Sits next to the Save button area so the relationship
            is obvious. */}
        {!isUnchanged && !hasParseError ? (
          <Alert severity="info" data-testid="page-detail-draft-modified">
            Modified — save before publishing.
          </Alert>
        ) : null}
        {hasParseError ? (
          <Alert severity="warning" data-testid="page-detail-draft-parse-warning">
            The Advanced JSON has unresolved errors — fix them or close the Advanced panel to
            continue.
          </Alert>
        ) : null}

        {/* Advanced toggle — preserves the raw JSON editor + schema panel
            for power users / debugging. Collapsed by default. Two-way
            binds to the block list above. */}
        <Box>
          <Button
            variant="text"
            size="small"
            onClick={() => setAdvancedOpen((v) => !v)}
            startIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            data-testid="page-detail-advanced-toggle"
          >
            Advanced (raw JSON + schema reference)
          </Button>
          <Collapse in={advancedOpen} unmountOnExit>
            <Stack spacing={2} sx={{ mt: 1 }} data-testid="page-detail-advanced-panel">
              {advancedError ? (
                <Alert severity="error" data-testid="page-detail-advanced-error">
                  {advancedError}
                </Alert>
              ) : null}
              <TextField
                label="Structure JSON (blocksOrderJson)"
                multiline
                minRows={10}
                maxRows={30}
                fullWidth
                value={advancedText}
                onChange={(e) => handleAdvancedTextChange(e.target.value)}
                error={advancedError !== null}
                helperText={
                  advancedError !== null
                    ? "Invalid JSON or schema — the block list above is preserved."
                    : isUnchanged
                      ? "Matches the persisted draft."
                      : "Two-way-bound with the block list above."
                }
                sx={{
                  "& textarea": {
                    fontFamily:
                      '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Monaco, monospace',
                    fontSize: 13,
                    lineHeight: 1.5,
                  },
                }}
                slotProps={{
                  input: {
                    spellCheck: false,
                    autoCorrect: "off",
                    autoCapitalize: "off",
                  },
                }}
                data-testid="page-detail-draft-json-input"
              />
              <SchemaReferenceCard />
            </Stack>
          </Collapse>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Button
            variant="outlined"
            onClick={() => setAiDialogOpen(true)}
            data-testid="page-detail-ai-generate-button"
          >
            Generate with AI
          </Button>
          {previewHostname ? (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => handlePreviewDraft(previewHostname)}
              disabled={createPreviewToken.isPending}
              data-testid="page-detail-draft-preview-button"
            >
              Preview draft
            </Button>
          ) : null}
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="page-detail-draft-save-button"
          >
            {update.isPending ? "Saving…" : "Save draft"}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleOpenPublishDialog}
            disabled={!canPublish}
            data-testid="page-detail-publish-button"
          >
            {publish.isPending ? "Publishing…" : "Publish"}
          </Button>
        </Stack>
      </Stack>
      <PageLayoutGeneratorDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        pageId={pageId}
        versionId={draft.id}
      />
      {/*
        Issue #243: publish dialog. Two radio choices:
          - "Make this the primary" → mode="primary" (DRAFT → PUBLISHED,
            demotes prior PUBLISHED → ARCHIVED, default).
          - "Publish for experiment use" → mode="experiment" (DRAFT →
            EXPERIMENT, no demotion; the row joins the experiment slot
            but the public site still renders the existing primary).
      */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        data-testid="page-detail-publish-dialog"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Publish draft</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pick how this draft should reach traffic. The primary version is what every visitor
              sees by default; experiment versions are only reachable through a running experiment.
            </Typography>
            <FormControl>
              <RadioGroup
                value={publishDialogMode}
                onChange={(e) => setPublishDialogMode(e.target.value as "primary" | "experiment")}
              >
                <FormControlLabel
                  value="primary"
                  control={<Radio data-testid="page-detail-publish-mode-primary" />}
                  label={
                    <Stack spacing={0.25}>
                      <Typography variant="body2">Make this the primary</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Replaces the currently-live version. The previous primary is archived.
                      </Typography>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value="experiment"
                  control={<Radio data-testid="page-detail-publish-mode-experiment" />}
                  label={
                    <Stack spacing={0.25}>
                      <Typography variant="body2">Publish for experiment use</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Joins the experiment slot, doesn&apos;t change the live page. Use this when
                        the version is a candidate variant.
                      </Typography>
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPublishDialogOpen(false)}
            data-testid="page-detail-publish-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmPublish}
            data-testid="page-detail-publish-dialog-confirm"
          >
            {publishDialogMode === "experiment" ? "Publish for experiment" : "Make primary"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/**
 * Published slot — read-only metadata view + Unpublish + Create-new-draft
 * actions. Hidden when there is no currently-published version.
 */
function PublishedSlot({
  pageId,
  published,
  onDraftCreated,
}: {
  pageId: string;
  published: PageVersionResponse;
  onDraftCreated: () => void;
}): ReactElement {
  const toast = useToast();
  const unpublish = useUnpublishPageVersion(pageId);
  const draftFrom = useDraftFromVersion(pageId);
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  const handleUnpublish = async () => {
    const ok = await confirm({
      title: "Unpublish this version?",
      body: "Unpublish this version? The public site will no longer render it.",
      confirmLabel: "Unpublish",
    });
    if (!ok) return;
    unpublish.mutate(
      { versionId: published.id },
      {
        onSuccess: () => toast.showSuccess("Version unpublished."),
        onError: (err) => toast.showError(err.message || "Could not unpublish."),
      },
    );
  };

  const handleDraftFrom = () => {
    draftFrom.mutate(
      { sourceVersionId: published.id },
      {
        onSuccess: () => {
          toast.showSuccess("New draft created from the published version.");
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not create draft."),
      },
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} data-testid="page-detail-published-slot">
      {confirmDialog}
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3">Currently published</Typography>
            <Chip
              size="small"
              color={statusChipColor(published.status)}
              label={published.status}
              variant="outlined"
              data-testid="page-detail-published-status-chip"
            />
            <Typography variant="body2" color="text.secondary">
              v{published.versionNumber}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Published {formatDateTime(published.publishedAt)}
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
          <Button
            color="warning"
            onClick={() => {
              void handleUnpublish();
            }}
            disabled={unpublish.isPending}
            data-testid="page-detail-unpublish-button"
          >
            {unpublish.isPending ? "Unpublishing…" : "Unpublish"}
          </Button>
          <Button
            variant="contained"
            onClick={handleDraftFrom}
            disabled={draftFrom.isPending}
            data-testid="page-detail-draft-from-published-button"
          >
            {draftFrom.isPending ? "Creating…" : "Create new draft from this"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Archived slot — collapsed by default. Reads from the optional
 * `archived` array on the page detail response (see top-of-file note);
 * when absent, renders an empty-state placeholder so the lifecycle row
 * always has a slot the operator can reason about.
 */
function ArchivedSlot({
  pageId,
  archived,
  onDraftCreated,
  defaultOpen = false,
  previewHostname = null,
}: {
  pageId: string;
  archived: ReadonlyArray<PageVersionResponse>;
  onDraftCreated: () => void;
  defaultOpen?: boolean;
  /**
   * Primary public hostname for this site, if any. When present the rows
   * expose a Preview affordance pointing at `/preview/page/:versionId`
   * so the operator can compare archived payloads visually rather than
   * by reading the JSON. The preview route is auth-gated and renders
   * any pageVersionId regardless of status (see #15 AC2).
   */
  previewHostname?: string | null;
}): ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  // Auto-expand when the parent flips `defaultOpen` to true after mount
  // (issue #186 dead-end state: unpublish promotes us from "archived
  // empty + collapsed" to "archived populated + must be visible"). React
  // ignores prop changes to `useState`'s initial value, so without this
  // effect the slot stays collapsed and the Restore CTA is unreachable
  // until the operator clicks the toggle. We only force-open here — we
  // never auto-collapse, so the operator's explicit toggle clicks are
  // preserved.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  const toast = useToast();
  const draftFrom = useDraftFromVersion(pageId);
  const createPreviewToken = useCreatePreviewToken();
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  // Issue #208: the preview URL needs a `?t=<jwt>` query param so the
  // public-host preview route can authenticate without an admin-host
  // cookie. We mint the token on click and open the tab when the
  // mutation resolves.
  const handlePreview = (versionId: string, hostname: string) => {
    createPreviewToken.mutate(
      { resourceType: "page-version", resourceId: versionId },
      {
        onSuccess: ({ token }) => {
          const url = buildPublicUrl(hostname, `/preview/page/${versionId}?t=${token}`);
          window.open(url, "_blank", "noopener,noreferrer");
        },
        onError: (err) => toast.showError(err.message || "Could not open preview."),
      },
    );
  };

  const handleRollback = async (version: PageVersionResponse) => {
    const ok = await confirm({
      title: `Roll back to v${String(version.versionNumber)}?`,
      body: `Roll back to v${String(version.versionNumber)}? A new draft will be created from this archived payload.`,
      confirmLabel: "Roll back",
      confirmColor: "warning",
    });
    if (!ok) return;
    draftFrom.mutate(
      { sourceVersionId: version.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Draft seeded from v${String(version.versionNumber)}.`);
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not roll back."),
      },
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} data-testid="page-detail-archived-slot">
      {confirmDialog}
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h3">Archived versions</Typography>
            <Typography variant="body2" color="text.secondary">
              {archived.length === 0 ? "None yet" : `${String(archived.length)} version(s)`}
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Collapse archived versions" : "Expand archived versions"}
            data-testid="page-detail-archived-toggle"
            disabled={archived.length === 0}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
        <Collapse in={open} unmountOnExit>
          <Stack spacing={1} divider={<Divider flexItem />}>
            {archived.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="page-detail-archived-empty"
              >
                No archived versions yet. Publishing a new draft archives the previously published
                one.
              </Typography>
            ) : (
              archived.map((version) => (
                <Stack
                  key={version.id}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  sx={{ py: 1 }}
                  data-testid="page-detail-archived-row"
                  data-version-id={version.id}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color={statusChipColor(version.status)}
                      label={version.status}
                      variant="outlined"
                    />
                    <Typography variant="body2">v{version.versionNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Created {formatDateTime(version.createdAt)}
                    </Typography>
                    {version.publishedAt ? (
                      <Typography variant="caption" color="text.secondary">
                        Published {formatDateTime(version.publishedAt)}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {previewHostname ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => handlePreview(version.id, previewHostname)}
                        disabled={createPreviewToken.isPending}
                        data-testid="page-detail-archived-preview-button"
                      >
                        Preview
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      onClick={() => {
                        void handleRollback(version);
                      }}
                      disabled={draftFrom.isPending}
                      data-testid="page-detail-archived-rollback-button"
                    >
                      Rollback
                    </Button>
                  </Stack>
                </Stack>
              ))
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

/**
 * Experiment versions slot (issue #243). Lists every EXPERIMENT-status
 * PageVersion newest-first. Each row exposes:
 *   - Promote → flips this row to PUBLISHED, demoting the prior
 *     PUBLISHED peer to ARCHIVED. Refused server-side with 423 if a
 *     RUNNING experiment still references the version (the operator
 *     must stop the experiment first).
 *   - Archive experiment → flips this row to ARCHIVED with no
 *     replacement, the "we tested it, didn't win" action.
 *   - Create draft from this → seeds a new DRAFT from the EXPERIMENT
 *     payload so the operator can iterate without losing the served
 *     version (EXPERIMENT rows are immutable like PUBLISHED).
 *
 * Hidden when there are no EXPERIMENT versions — the slot only renders
 * when there's something to show.
 */
function ExperimentVersionsSlot({
  pageId,
  experimentVersions,
  onDraftCreated,
  previewHostname = null,
}: {
  pageId: string;
  experimentVersions: ReadonlyArray<PageVersionResponse>;
  onDraftCreated: () => void;
  /**
   * Primary public hostname for this site, if any. When present each
   * row exposes a Preview affordance pointing at `/preview/page/:versionId`
   * (issue #251) so the operator can see the EXPERIMENT payload rendered
   * without having to bucket into the matching variant on a RUNNING
   * experiment. The preview route is auth-gated and renders any
   * pageVersionId regardless of status — see
   * `findPageVersionById` in `renderer.repository.ts` which intentionally
   * bypasses status filtering for the preview surface.
   */
  previewHostname?: string | null;
}): ReactElement | null {
  const toast = useToast();
  const promote = usePromotePageVersion(pageId);
  const archiveExperiment = useArchiveExperimentPageVersion(pageId);
  const draftFrom = useDraftFromVersion(pageId);
  const createPreviewToken = useCreatePreviewToken();
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  if (experimentVersions.length === 0) return null;

  // Issue #251: mirror the ArchivedSlot Preview pattern — mint a
  // short-lived JWT, then open the public-host preview URL with `?t=...`
  // appended. Errors surface via the existing toast machinery instead of
  // opening a broken tab.
  const handlePreview = (versionId: string, hostname: string) => {
    createPreviewToken.mutate(
      { resourceType: "page-version", resourceId: versionId },
      {
        onSuccess: ({ token }) => {
          const url = buildPublicUrl(hostname, `/preview/page/${versionId}?t=${token}`);
          window.open(url, "_blank", "noopener,noreferrer");
        },
        onError: (err) => toast.showError(err.message || "Could not open preview."),
      },
    );
  };

  const handlePromote = async (version: PageVersionResponse) => {
    const ok = await confirm({
      title: `Promote v${String(version.versionNumber)} to primary?`,
      body: `Promote v${String(version.versionNumber)} to primary? The current primary will be archived.`,
      confirmLabel: "Promote",
      confirmColor: "primary",
    });
    if (!ok) return;
    promote.mutate(
      { versionId: version.id },
      {
        onSuccess: () => toast.showSuccess("Version promoted to primary."),
        onError: (err: ApiClientError) => {
          if (err.code === "VERSION_LOCKED_BY_EXPERIMENT") {
            toast.show({
              message: "This version is locked by a running experiment. Stop the experiment first.",
              severity: "warning",
            });
            return;
          }
          toast.showError(err.message || "Could not promote version.");
        },
      },
    );
  };

  const handleArchiveExperiment = async (version: PageVersionResponse) => {
    const ok = await confirm({
      title: `Archive experiment v${String(version.versionNumber)}?`,
      body: `Archive experiment v${String(version.versionNumber)}? It will no longer be available as a variant target.`,
      confirmLabel: "Archive",
    });
    if (!ok) return;
    archiveExperiment.mutate(
      { versionId: version.id },
      {
        onSuccess: () => toast.showSuccess("Experiment version archived."),
        onError: (err: ApiClientError) => {
          if (err.code === "VERSION_LOCKED_BY_EXPERIMENT") {
            toast.show({
              message: "This version is locked by a running experiment. Stop the experiment first.",
              severity: "warning",
            });
            return;
          }
          toast.showError(err.message || "Could not archive experiment version.");
        },
      },
    );
  };

  const handleDraftFrom = (version: PageVersionResponse) => {
    draftFrom.mutate(
      { sourceVersionId: version.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Draft seeded from experiment v${String(version.versionNumber)}.`);
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not create draft."),
      },
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="page-detail-experiment-versions-slot"
    >
      {confirmDialog}
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h3">Experiment versions</Typography>
          <Typography variant="body2" color="text.secondary">
            {experimentVersions.length} version(s)
          </Typography>
          <Tooltip title="Versions approved for experiment traffic. The default render is unaffected.">
            <Chip size="small" variant="outlined" label="info" />
          </Tooltip>
        </Stack>
        <Stack spacing={1} divider={<Divider flexItem />}>
          {experimentVersions.map((version) => (
            <Stack
              key={version.id}
              spacing={0.5}
              sx={{ py: 1 }}
              data-testid="page-detail-experiment-row"
              data-version-id={version.id}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={statusChipColor(version.status)}
                    label={version.status}
                    variant="outlined"
                  />
                  <Typography variant="body2">v{version.versionNumber}</Typography>
                  {version.publishedAt ? (
                    <Typography variant="caption" color="text.secondary">
                      Published {formatDateTime(version.publishedAt)}
                    </Typography>
                  ) : null}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {previewHostname ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      onClick={() => handlePreview(version.id, previewHostname)}
                      disabled={createPreviewToken.isPending}
                      data-testid="page-detail-experiment-preview-button"
                    >
                      Preview
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDraftFrom(version)}
                    disabled={draftFrom.isPending}
                    data-testid="page-detail-experiment-draft-from-button"
                  >
                    Create draft
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => {
                      void handleArchiveExperiment(version);
                    }}
                    disabled={archiveExperiment.isPending}
                    data-testid="page-detail-experiment-archive-button"
                  >
                    Archive
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      void handlePromote(version);
                    }}
                    disabled={promote.isPending}
                    data-testid="page-detail-experiment-promote-button"
                  >
                    Promote to primary
                  </Button>
                </Stack>
              </Stack>
              {/* Issue #255 AC1 — backlinks render under the status chip
                  so the operator can see which experiment(s) consume this
                  EXPERIMENT version without hunting through the experiments
                  list. Empty list → muted "Not referenced" so an orphaned
                  row is obviously safe to archive. */}
              <ExperimentBacklinks
                refs={version.referencingExperiments ?? []}
                testIdPrefix="page-detail-experiment-row-backlink"
              />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Recovery card shown in place of the draft slot when no draft exists.
 * Issue #186: after publish (draft → PUBLISHED) and especially after
 * unpublish (PUBLISHED → ARCHIVED with no replacement), the operator
 * needs an obvious one-click path back to an editable surface. When a
 * `recoveryTarget` is supplied we surface a primary CTA that calls
 * `draft-from` against it; otherwise we degrade to the legacy informational
 * alert (still reachable from the PublishedSlot's "Create new draft from
 * this" button on pages that have a published version).
 */
function NoDraftRecoveryCard({
  pageId,
  recoveryTarget,
  onDraftCreated,
}: {
  pageId: string;
  recoveryTarget: PageVersionResponse | null;
  onDraftCreated: () => void;
}): ReactElement {
  const toast = useToast();
  const draftFrom = useDraftFromVersion(pageId);

  if (!recoveryTarget) {
    return (
      <Alert severity="info" data-testid="page-detail-no-draft">
        No draft version. Use &quot;Create new draft from this&quot; on the published version below
        to start editing.
      </Alert>
    );
  }

  const handleRestore = () => {
    draftFrom.mutate(
      { sourceVersionId: recoveryTarget.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Draft restored from v${String(recoveryTarget.versionNumber)}.`);
          onDraftCreated();
        },
        onError: (err) => toast.showError(err.message || "Could not restore draft."),
      },
    );
  };

  const sourceLabel =
    recoveryTarget.status === "ARCHIVED"
      ? `archived v${String(recoveryTarget.versionNumber)}`
      : `v${String(recoveryTarget.versionNumber)}`;

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid="page-detail-no-draft-recovery"
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h3">No active draft</Typography>
          <Typography variant="body2" color="text.secondary">
            This page has no draft to edit. Restore the most recent {sourceLabel} as a new draft to
            keep iterating — the archived payload is copied into a fresh DRAFT row, leaving the
            archived version untouched.
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleRestore}
            disabled={draftFrom.isPending}
            data-testid="page-detail-restore-archived-button"
          >
            {draftFrom.isPending ? "Restoring…" : `Restore ${sourceLabel} as draft`}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Schema-reference card — read-only documentation of the `blocksOrderV1`
 * envelope. Rendered inline inside the DraftSlot's Advanced panel (issue
 * #256 moved the schema panel from the page's top level to here, behind
 * the Advanced toggle, so it's only visible to operators who opted in to
 * the power-user view).
 *
 * Sourced from the shared Zod schema (see `SCHEMA_REFERENCE_EXAMPLE_TEXT`)
 * so the displayed example is guaranteed to be a valid payload; the
 * field descriptions are short and stable enough to live as a literal
 * list here.
 */
function SchemaReferenceCard(): ReactElement {
  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 }, bgcolor: "background.default" }}
      data-testid="page-detail-schema-panel"
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1">Schema reference: blocksOrderV1</Typography>
          <Chip size="small" variant="outlined" label="read-only" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          The draft JSON above must match the <code>blocksOrderV1</code> envelope. Use the block
          list editor above to look up references — duplicates are allowed and the order matters.
        </Typography>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Fields
          </Typography>
          <Stack
            component="ul"
            spacing={0.5}
            sx={{ pl: 3, m: 0 }}
            data-testid="page-detail-schema-panel-fields"
          >
            <Typography component="li" variant="body2">
              <code>schemaVersion</code> — required literal <code>1</code>. Reserved hinge for
              future shape migrations.
            </Typography>
            <Typography component="li" variant="body2">
              <code>blocks</code> — ordered array (max 100). Each entry is{" "}
              <code>{"{ contentItemId: string }"}</code> referencing an active ContentItem on this
              site.
            </Typography>
            <Typography component="li" variant="body2">
              <code>blocks[].contentItemId</code> — non-empty string. Server rejects unknown ids
              with <code>INVALID_BLOCKS_ORDER</code>.
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Example
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Monaco, monospace',
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre",
              overflowX: "auto",
            }}
            data-testid="page-detail-schema-panel-example"
          >
            {SCHEMA_REFERENCE_EXAMPLE_TEXT}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

export function PageDetailPage(): ReactElement {
  // Route id is `/authenticated/sites/$siteId/pages/$pageId` because the
  // route lives beneath the pathless `authenticated` layout route
  // (see router.tsx).
  const { siteId, pageId } = useParams({
    from: "/authenticated/sites/$siteId/pages/$pageId",
  });

  const site = useSite(siteId);
  const page = usePage(pageId);
  // #184: preview link in the header. Cache-shared with SiteDetailPage's
  // `useDomains` call so this is a no-op extra read when navigating from
  // the Pages tab.
  const domains = useDomains(siteId);
  const primaryDomain = findPrimaryDomain(domains.data);
  const draftRef = useRef<HTMLDivElement | null>(null);
  // Header-level toast + preview-token mutation. Used by the "Inspect"
  // button which mints a #208 preview token against the PUBLISHED
  // PageVersion id and opens the public URL with `?_inspect=<token>`.
  const headerToast = useToast();
  const headerPreviewToken = useCreatePreviewToken();

  // Draft block list — the in-memory source of truth for the structured
  // editor (issue #256). Lifted to the page-level so the value survives
  // remounts of the draft slot (e.g. an AI generate that flips the
  // version id and re-renders the slot subtree). `DraftSlot` reads the
  // server-side seed and resyncs `blocks` whenever the draft payload
  // changes — local edits stay local until Save.
  const [draftBlocks, setDraftBlocks] = useState<BlockRef[]>([]);

  // After a successful draft-from-version mutation we want the operator
  // to see the new draft immediately. The mutation invalidates the
  // detail query so the new draft surfaces in the response; we then
  // scroll the draft slot into view so the operator never has to hunt
  // for it. Captured as a stable callback so child slots can fire it.
  const scrollToDraft = () => {
    draftRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (page.isPending) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
        data-testid="page-detail-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (page.isError) {
    return (
      <Alert severity="error" data-testid="page-detail-error">
        {page.error.message}
      </Alert>
    );
  }

  if (!page.data) {
    return (
      <Alert severity="warning" data-testid="page-detail-missing">
        Page not found.
      </Alert>
    );
  }

  const detail: PageDetail = page.data;
  const { page: pageRow, latestDraft, published, experimentVersions, archived } = detail;

  // Issue #186: when the operator has no draft, pick a recovery source so
  // they can mint a new draft in one click. Prefer the most-recent archived
  // row (covers the unpublish dead-end, since unpublish ARCHIVEs the
  // formerly-published version). If there are no archived rows but a
  // published exists, fall back to it — the PublishedSlot has its own
  // "Create new draft from this" CTA, so we leave `recoveryTarget = null`
  // and let the helper alert point operators there.
  const recoveryTarget: PageVersionResponse | null = latestDraft ? null : (archived[0] ?? null);
  const isDeadEnd = !latestDraft && !published && archived.length > 0;

  return (
    <Stack spacing={3} data-testid="page-detail-page">
      <Box>
        <Typography variant="body2" color="text.secondary">
          <Link to="/sites">Sites</Link> /{" "}
          <Link to="/sites/$siteId" params={{ siteId }}>
            {site.data?.name ?? siteId}
          </Link>{" "}
          / {pageRow.path}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mt: 1 }}
        >
          <Typography variant="h2" data-testid="page-detail-path">
            {pageRow.path}
          </Typography>
          <Chip
            size="small"
            label={pageRow.type}
            variant="outlined"
            data-testid="page-detail-type-chip"
          />
          {/*
           * This button opens the *live* published page on the public host
           * — pageRow.path resolves through the public renderer's
           * `/<path>` route, which only returns the currently-PUBLISHED
           * version. It is NOT a preview surface (no auth-gated route, no
           * draft/archived support), so the label says so. Hidden when
           * there's nothing live to view; previewing unpublished states
           * is handled by the per-version Preview buttons further down.
           */}
          {primaryDomain && published ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              component="a"
              href={buildPublicUrl(primaryDomain.hostname, pageRow.path)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="page-detail-view-published-button"
            >
              View published
            </Button>
          ) : null}
          {primaryDomain && published ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon fontSize="small" />}
              onClick={() => {
                /*
                 * Inspect mode = published page + the preview-mode hover
                 * outline (#235). We mint a #208 preview token against the
                 * PUBLISHED PageVersion id, then open the public URL with
                 * `?_inspect=<token>` — the catch-all renderer validates
                 * the token and flips `preview: true`, which lights up the
                 * hover-outline + label CSS rules. Anyone pasting a random
                 * token gets a normal public render.
                 */
                headerPreviewToken.mutate(
                  { resourceType: "page-version", resourceId: published.id },
                  {
                    onSuccess: ({ token }) => {
                      const url =
                        buildPublicUrl(primaryDomain.hostname, pageRow.path) +
                        `?_inspect=${encodeURIComponent(token)}`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    },
                    onError: (err) =>
                      headerToast.showError(err.message || "Could not open inspect."),
                  },
                );
              }}
              disabled={headerPreviewToken.isPending}
              data-testid="page-detail-inspect-published-button"
            >
              Inspect
            </Button>
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Created {formatDateTime(pageRow.createdAt)} · Updated {formatDateTime(pageRow.updatedAt)}
        </Typography>
      </Box>

      {/*
       * Slot order, top → bottom:
       *   1. Published    — what's currently live; carries the "Create new
       *      draft from this" CTA which is the most common starting point.
       *   2. Draft        — the working surface (or the recovery card when
       *      there's nothing to edit yet). Issue #256 folded the editor
       *      helpers (raw JSON textarea, schema reference) into a single
       *      Advanced toggle inside the draft slot, and replaced the
       *      standalone ContentItems browser with an inline Add Autocomplete.
       *   3. Experiments  — issue #243 EXPERIMENT-status versions.
       *   4. Archived     — collapsible history, with a per-row Preview link.
       *
       * The Published-first ordering is deliberate: an operator landing on
       * the page sees the live state first and the affordance to start a
       * new draft below it, instead of an editor with no obvious context.
       */}
      {published ? (
        <PublishedSlot pageId={pageId} published={published} onDraftCreated={scrollToDraft} />
      ) : (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 } }}
          data-testid="page-detail-no-published-slot"
        >
          <Stack spacing={1}>
            <Typography variant="h3">Currently published</Typography>
            <Typography variant="body2" color="text.secondary">
              {archived.length > 0
                ? "This page has no live version. Restore an archived version below as a draft, then publish it to bring the page back online."
                : "This page has no published version yet. Publish a draft below to make it visible on the public site."}
            </Typography>
          </Stack>
        </Paper>
      )}

      <Box ref={draftRef}>
        {latestDraft ? (
          <DraftSlot
            pageId={pageId}
            siteId={siteId}
            draft={latestDraft}
            blocks={draftBlocks}
            setBlocks={setDraftBlocks}
            onAfterDraftFromPublished={() => {
              void page.refetch();
            }}
            previewHostname={primaryDomain?.hostname ?? null}
          />
        ) : (
          <NoDraftRecoveryCard
            pageId={pageId}
            recoveryTarget={recoveryTarget}
            onDraftCreated={() => {
              void page.refetch();
            }}
          />
        )}
      </Box>

      {/* Issue #243: experiment versions sit between the draft surface
          and the archive — operators reading the lifecycle row top→bottom
          see Primary → Draft → Experiments → Archive. Hidden when there
          are no EXPERIMENT rows (the slot is self-empty-state-aware). */}
      <ExperimentVersionsSlot
        pageId={pageId}
        experimentVersions={experimentVersions}
        onDraftCreated={scrollToDraft}
        previewHostname={primaryDomain?.hostname ?? null}
      />

      <ArchivedSlot
        pageId={pageId}
        archived={archived}
        onDraftCreated={scrollToDraft}
        defaultOpen={isDeadEnd}
        previewHostname={primaryDomain?.hostname ?? null}
      />
    </Stack>
  );
}
